import type {
    SketchRegionGraphEdge,
    SketchRegionSourceSegment,
    SketchRegionVec2,
} from './SketchRegionTypes';
import {
    crossVec2,
    lerpVec2,
    lerpVec3,
    pointsAlmostEqual2,
    subtractVec2,
    vertexKey,
} from './SketchRegionMath';

/** 线段拆分容差。 */
const SPLIT_EPSILON = 1e-8;

/**
 * 计算线段参数。
 * @param point 线段上的点。
 * @param start 起点。
 * @param end 终点。
 * @returns 参数 t。
 */
function parameterOnSegment(
    point: SketchRegionVec2,
    start: SketchRegionVec2,
    end: SketchRegionVec2,
): number {
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const denominator = Math.abs(dx) >= Math.abs(dy) ? dx : dy;
    if (Math.abs(denominator) <= SPLIT_EPSILON) {
        return 0;
    }
    return Math.abs(dx) >= Math.abs(dy)
        ? (point[0] - start[0]) / denominator
        : (point[1] - start[1]) / denominator;
}

/**
 * 判断参数是否在线段内。
 * @param value 参数。
 * @returns 是否在线段内。
 */
function isUnitParameter(value: number): boolean {
    return value >= -SPLIT_EPSILON && value <= 1 + SPLIT_EPSILON;
}

/**
 * 收敛线段参数到 [0, 1]。
 * @param value 参数。
 * @returns 收敛后的参数。
 */
function clampUnit(value: number): number {
    return Math.min(1, Math.max(0, value));
}

/**
 * 计算两条线段的交点参数。
 * @param left 左线段。
 * @param right 右线段。
 * @returns 左右线段上的交点参数列表。
 */
function intersectSegmentParameters(
    left: SketchRegionSourceSegment,
    right: SketchRegionSourceSegment,
): Array<{ leftT: number; rightT: number }> {
    const p = left.start;
    const r = subtractVec2(left.end, left.start);
    const q = right.start;
    const s = subtractVec2(right.end, right.start);
    const denominator = crossVec2(r, s);
    const qMinusP = subtractVec2(q, p);

    if (Math.abs(denominator) <= SPLIT_EPSILON) {
        if (Math.abs(crossVec2(qMinusP, r)) > SPLIT_EPSILON) {
            return [];
        }
        const candidates = [right.start, right.end, left.start, left.end];
        return candidates.flatMap((candidate) => {
            const leftT = parameterOnSegment(candidate, left.start, left.end);
            const rightT = parameterOnSegment(candidate, right.start, right.end);
            return isUnitParameter(leftT) && isUnitParameter(rightT)
                ? [{ leftT: clampUnit(leftT), rightT: clampUnit(rightT) }]
                : [];
        });
    }

    const leftT = crossVec2(qMinusP, s) / denominator;
    const rightT = crossVec2(qMinusP, r) / denominator;
    return isUnitParameter(leftT) && isUnitParameter(rightT)
        ? [{ leftT: clampUnit(leftT), rightT: clampUnit(rightT) }]
        : [];
}

/**
 * 去重并排序线段参数。
 * @param parameters 参数集合。
 * @returns 规范参数集合。
 */
function normalizeParameters(parameters: number[]): number[] {
    const sorted = parameters
        .map(clampUnit)
        .sort((left, right) => left - right);
    return sorted.reduce<number[]>((result, parameter) => {
        const previous = result[result.length - 1];
        if (previous === undefined || Math.abs(previous - parameter) > SPLIT_EPSILON) {
            result.push(parameter);
        }
        return result;
    }, []);
}

/**
 * 合并重复图边。
 * @param edges 原始图边。
 * @returns 合并后的图边。
 */
function mergeDuplicateEdges(edges: SketchRegionGraphEdge[]): SketchRegionGraphEdge[] {
    const edgeMap = new Map<string, SketchRegionGraphEdge>();
    edges.forEach((edge) => {
        const key = edge.startKey < edge.endKey
            ? `${edge.startKey}|${edge.endKey}`
            : `${edge.endKey}|${edge.startKey}`;
        const existing = edgeMap.get(key);
        if (!existing) {
            edgeMap.set(key, {
                ...edge,
                entityIds: [...edge.entityIds],
                closedEntityIds: [...edge.closedEntityIds],
                openEntityIds: [...edge.openEntityIds],
            });
            return;
        }
        existing.entityIds = Array.from(new Set([...existing.entityIds, ...edge.entityIds])).sort();
        existing.closedEntityIds = Array.from(new Set([...existing.closedEntityIds, ...edge.closedEntityIds])).sort();
        existing.openEntityIds = Array.from(new Set([...existing.openEntityIds, ...edge.openEntityIds])).sort();
    });
    return Array.from(edgeMap.values());
}

/**
 * 按交点拆分草图线段。
 * @param segments 原始线段。
 * @returns 平面图边集合。
 */
export function splitSketchRegionSegments(
    segments: SketchRegionSourceSegment[],
): SketchRegionGraphEdge[] {
    const splitParameters = segments.map(() => [0, 1]);

    for (let leftIndex = 0; leftIndex < segments.length; leftIndex += 1) {
        for (let rightIndex = leftIndex + 1; rightIndex < segments.length; rightIndex += 1) {
            const left = segments[leftIndex]!;
            const right = segments[rightIndex]!;
            intersectSegmentParameters(left, right).forEach((intersection) => {
                splitParameters[leftIndex]!.push(intersection.leftT);
                splitParameters[rightIndex]!.push(intersection.rightT);
            });
        }
    }

    const edges = segments.flatMap((segment, segmentIndex) => {
        const parameters = normalizeParameters(splitParameters[segmentIndex]!);
        const result: SketchRegionGraphEdge[] = [];
        for (let index = 0; index < parameters.length - 1; index += 1) {
            const startT = parameters[index]!;
            const endT = parameters[index + 1]!;
            if (endT - startT <= SPLIT_EPSILON) {
                continue;
            }
            const start = lerpVec2(segment.start, segment.end, startT);
            const end = lerpVec2(segment.start, segment.end, endT);
            if (pointsAlmostEqual2(start, end)) {
                continue;
            }
            const startKey = vertexKey(start);
            const endKey = vertexKey(end);
            if (startKey === endKey) {
                continue;
            }
            result.push({
                startKey,
                endKey,
                start,
                end,
                start3: lerpVec3(segment.start3, segment.end3, startT),
                end3: lerpVec3(segment.start3, segment.end3, endT),
                entityIds: [segment.entityId],
                closedEntityIds: segment.closedEntity ? [segment.entityId] : [],
                openEntityIds: segment.closedEntity ? [] : [segment.entityId],
            });
        }
        return result;
    });

    return mergeDuplicateEdges(edges);
}
